// Is this web app running inside the innernet Mac app shell? The shell exposes
// a minimal `window.innernetDesktop` bridge and tags its user-agent with
// `innernetDesktop/<ver>`. Detecting it lets the UI inset the top bar under the
// traffic lights and turn the chrome into a window-drag handle. Harmless (and
// false) in a normal browser.
export function isDesktopShell(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { innernetDesktop?: unknown };
  if (typeof w.innernetDesktop !== "undefined") return true;
  return typeof navigator !== "undefined" && / innernetDesktop\//.test(navigator.userAgent);
}

// ── THE bridge, one type ──────────────────────────────────────────────────────
// What the Mac app's preload puts on `window.innernetDesktop` (on every page the
// app window loads, innernet.live included). The preload is checked against this
// type (`satisfies DesktopBridge`, a type-only import through the desktop's
// `web/*` alias), so the web and the app can never disagree about it. Every
// method past the first three is optional: an older build simply lacks it, and
// a surface asks for the method before it offers the control (`b?.deskStatus`).

import type { ShellNetti } from "../components/os/OsShellContext";

/** what each of netti's hands on the Mac may do: run · ask first · never */
export type DeskHandMode = "on" | "ask" | "off";

/** the person's corner settings, as the app stores them (apps/desktop/src/desk/settings.ts) */
export interface DeskPrefs {
  enabled: boolean;
  rest: "peek" | "hidden";
  summon: "near" | "corner" | "key";
  dwellMs: number;
  takeHotCorner: boolean;
  savedHotCorner: number | null;
  savedHotCornerModifier: number | null;
  hotkey: string;
  frost: "soft" | "standard" | "deep";
  railWidth: "narrow" | "standard" | "wide";
  notices: boolean;
  background: boolean;
  sounds: boolean;
  computerUse: boolean;
  hands: Record<string, DeskHandMode>;
}

/** one of netti's hands, as the installed build describes it */
export interface DeskHand {
  name: string;
  /** what it does, in a line a person reads */
  line: string;
  /** the floor the hand itself sets: free to run, free inside the bench, or always asks */
  floor: "free" | "workbench" | "ask";
  /** what macOS must allow first, if anything */
  needs?: "accessibility" | "screen" | "automation";
  /** moves the mouse or the keyboard (off until computer use is on) */
  computer?: boolean;
  /** a thing to say to netti that shows the hand working */
  try?: string;
}

export interface DeskStatus {
  /** the corner can run on this machine (macOS, a build that has it) */
  supported: boolean;
  prefs: DeskPrefs;
  /** the key that actually bound ("" when every candidate was taken) */
  boundHotkey: string;
  /** the server says the corner is not open to this person yet */
  gated: boolean;
  /** what macOS has on the bottom-right hot corner now, by number and in words */
  systemHotCorner: { value: number | null; label: string };
  permissions: { accessibility: boolean; screen: boolean };
  /** the native frost draws a graded blur (else a masked system blur) */
  graded: boolean;
  hands: DeskHand[];
  version: string;
  // ── 0.6.0 and later (an older app leaves these out: the page reads each one optionally) ──
  /** every permission the desk can need, as its own row */
  permissionRows?: DeskPermissionRow[];
  /** what netti can see on this Mac right now, one thing per row */
  sees?: DeskSeesRow[];
  /** what netti never does, each held by the app's code */
  never?: string[];
  /** what of a run stays in the person's memory, said plainly */
  keeps?: string;
  /** the walks netti can take the person through, and how far they got */
  walks?: { setup: DeskWalkProgress; whatsNew: (DeskWalkProgress & { version: string }) | null };
}

export type DeskPermissionKey = "accessibility" | "screen" | "automation" | "files" | "notifications";

/** one permission: allowed · not-yet (macOS says no) · asks (macOS asks the first time) · unused (nothing needs it now) */
export interface DeskPermissionRow {
  key: DeskPermissionKey;
  label: string;
  why: string;
  state: "allowed" | "not-yet" | "asks" | "unused";
  /** the one thing the row offers: ask macOS now, or open its page of System Settings */
  action: "allow" | "open" | null;
}

/** one thing on the Mac, and how netti may touch it: runs (when asked) · asks first · never · blocked (macOS has not allowed it) */
export interface DeskSeesRow {
  id: string;
  label: string;
  value: string;
  tone: "runs" | "asks" | "never" | "blocked";
}

export interface DeskWalkProgress {
  done: number;
  of: number;
  finished: boolean;
  started: boolean;
}

/** a change that lets netti do MORE without asking is confirmed by the app itself, natively */
export type DeskSetResult = { ok: true; status: DeskStatus } | { ok: false; declined?: boolean; error?: string };

export interface DesktopBridge {
  version(): Promise<string>;
  platform: string;
  shell: "desktop";
  capture?(text?: string): Promise<unknown>;
  openResidence?(): Promise<void>;
  openExternal?(url: string): Promise<void>;
  onHotkey?(cb: () => void): () => void;
  updateStatus?(): Promise<unknown>;
  checkForUpdate?(): Promise<void>;
  installUpdate?(): Promise<void>;
  onUpdateStatus?(cb: (s: unknown) => void): () => void;
  safariStatus?(): Promise<unknown>;
  openSafariSettings?(): Promise<{ ok: boolean; error?: string }>;
  wallpaperStatus?(): Promise<{ supported: boolean; on: boolean }>;
  setWallpaper?(on: boolean): Promise<{ supported: boolean; on: boolean }>;
  residence?(): Promise<{ active: string; cloudPaired: boolean }>;
  switchResidence?(): Promise<void>;
  getLoginItem?(): Promise<boolean>;
  setLoginItem?(on: boolean): Promise<void>;
  readClipboard?(): Promise<string>;
  captureLocal?(text: string): Promise<unknown>;
  getShortcut?(): Promise<string>;
  setShortcut?(accel: string): Promise<{ ok: boolean; accelerator: string }>;

  // ── netti on the desk (settings → netti on the desk) ──
  deskStatus?(): Promise<DeskStatus>;
  /** apply a patch; widening changes are confirmed natively and may come back declined */
  setDesk?(patch: Partial<DeskPrefs>): Promise<DeskSetResult>;
  onDeskStatus?(cb: (s: DeskStatus) => void): () => void;
  /** show the corner doing one thing: rise, settle, a notice, or a real ask */
  deskTry?(what: "summon" | "rest" | "notice" | "ask", text?: string): Promise<void>;
  /** ask macOS for a permission (the system prompt) or open its pane in System Settings */
  deskPermission?(which: DeskPermissionKey, how: "prompt" | "open"): Promise<DeskStatus["permissions"]>;
  /** netti walks the person through it in the corner: the setup, or what is new in this build (0.6.0+) */
  deskWalk?(which: "setup" | "whatsnew"): Promise<boolean>;
  /** hand the dashboard's netti to the corner (law_one_netti) */
  deskNetti?(netti: ShellNetti | null): void;
}

/** the bridge, when the app is there */
export function desktopBridge(): DesktopBridge | null {
  if (typeof window === "undefined") return null;
  return ((window as unknown as { innernetDesktop?: DesktopBridge }).innernetDesktop ?? null) as DesktopBridge | null;
}
